import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Box, Breadcrumbs } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { generatePath, Link } from 'react-router-dom';
import Bluebird from 'bluebird';
import { FormContext, useForm } from 'react-hook-form';
import useActiveUser from '../../../hooks/useActiveUser';
import { getUserRolesInCurrentlySelectedSite } from '../../../services/auth';
import { Roles } from '../../../models/roles';
import ReceiverNeedsProfile from '../../../components/ReceiverNeedsProfile';
import { newFoodTypes } from '../../../models/foodTypes';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import snackbarHelper from '../../../helpers/snackbarHelper';
import {
  fetchReceiverNeedsProfile,
  fetchReceiversIfNeeded,
  updateReceiverFoodPreferences,
} from '../../../actions/receivers';
import ButtonWithLoading from '../../../components/ButtonWithLoading';
import { fetchFoodDescriptionsIfNeeded } from '../../../actions/foodDescriptions';

const ReceiverMobileFoodPreferencesBreadcrumb = () => {
  const { formatMessage } = useIntl();

  return (
    <Box boxShadow={0}>
      <Breadcrumbs aria-label="breadcrumb">
        <StyledRescuerBreadcrumb
          component={Link}
          to={generatePath(routes.mobileDashboard)}
          label={formatMessage({ id: 'breadcrumbs.home' })}
          icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
        />
        <StyledRescuerTextBreadcrumb
          component="a"
          href="#"
          label={formatMessage({ id: 'breadcrumbs.receiver-food-preferences' })}
        />
      </Breadcrumbs>
    </Box>
  );
};

const ReceiverMobileFoodPreferences = () => {
  const activeUser = useActiveUser();
  const dispatch = useDispatch();
  const userRoles = getUserRolesInCurrentlySelectedSite(activeUser);
  const rolesWithAccess = userRoles.filter(
    (role) => role.role_name === Roles.ReceiverLocationAdmin || role.role_name === Roles.ReceiverLocationStaff
  );
  const receiverId = rolesWithAccess[0]?.receiver_id;
  const receivers = useSelector((state) => state.entities.receivers);
  const locations = receivers.locations.byReceiverId?.[receiverId] || [];
  const receiverLocation = locations[0];
  const foodDescriptionsEntities = useSelector((state) => state.entities.foodDescriptions);
  const receiverNeedsProfileEntities = useSelector((state) => state.entities.receivers.needsProfile);
  const receiverNeedsProfile = receiverNeedsProfileEntities.byLocationId[receiverLocation?.id] || [];
  const foodDescriptions = Object.values(foodDescriptionsEntities.byId);
  const categories = foodDescriptions.filter((fd) => newFoodTypes.some((ft) => ft.label === fd.name));
  const [needsInitialValues, setNeedsInitialValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formMethods = useForm({
    defaultValues: {
      needs_profile: needsInitialValues,
    },
  });
  const {
    handleSubmit,
    reset,
    getValues,
    formState: { dirtyFields },
  } = formMethods;

  useEffect(() => {
    dispatch(fetchReceiversIfNeeded());
    dispatch(fetchFoodDescriptionsIfNeeded());
  }, []);

  useEffect(() => {
    if (receiverLocation?.id) {
      dispatch(fetchReceiverNeedsProfile(receiverId, receiverLocation.id));
    }
  }, [receiverLocation?.id]);

  useEffect(() => {
    if (receiverNeedsProfile.length && foodDescriptions.length) {
      const needs = Object.assign(
        {},
        ...categories.map((fd) => {
          const need = receiverNeedsProfile.find((n) => n.food_description_id === fd.id);
          return { [fd.id]: need?.rating || 0 };
        })
      );

      reset({
        needs_profile: needs,
      });

      setNeedsInitialValues(needs);
    }
  }, [receiverNeedsProfileEntities.inflight, foodDescriptionsEntities.inflight]);

  const submitHandler = (data) => {
    dispatch(updateReceiverFoodPreferences(receiverId, receiverLocation.id, data));
  };

  const onSubmit = (values) => {
    const needs = Object.entries(values.needs_profile)
      .map(([key, value]) => ({
        rating: value,
        food_description_id: Number(key),
      }))
      .filter((value) => value.rating !== undefined);

    const data = {
      ...receiverLocation,
      needs_profile: needs,
    };

    setIsSubmitting(true);

    return Bluebird.try(() => submitHandler(data))
      .then(() => {
        snackbarHelper.success('Food Preferences saved successfully!');
        setNeedsInitialValues(values.needs_profile);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => {
        setIsSubmitting(false);
        reset(getValues(), { isSubmitted: true })
      });
  };

  return (
    <>
      <ReceiverMobileFoodPreferencesBreadcrumb />

      <Box px={1} py={1}>
        <FormContext {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ReceiverNeedsProfile categories={categories} initialValues={needsInitialValues} isMobileView />

            <Box py={1}>
              <ButtonWithLoading
                fullWidth
                disabled={!dirtyFields.size}
                type="submit"
                variant="contained"
                color="primary"
                isLoading={isSubmitting}
              >
                Save
              </ButtonWithLoading>
            </Box>
          </form>
        </FormContext>
      </Box>
    </>
  );
};

export default ReceiverMobileFoodPreferences;
